import {
  Badge,
  Dot,
  Popover,
  PopoverChevron,
  PopoverTrigger,
  Skeleton,
  Typography
} from '@sollapay/ui/components';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';

import {showResultToastCard} from '@/components';
import {getTranslatedErrorMessage} from '@/utils';

import {DEFAULT_ROLE_DOT_COLOR_CLASS, ROLE_DOT_COLOR_CLASS} from '../config';
import {useChangeMemberRoleMutation, useRolesQuery} from '../hooks';
import {TeamMemberRolePopoverContent} from './TeamMemberRolePopoverContent';

import type {RoleDTO} from '@sollapay/types';
import type {FC} from 'react';

export interface TeamMemberRoleSelectProps {
  memberId: string;
  roleKey: string;
  /** Renders the resolved role label as plain text — no permission to change it. */
  readOnly: boolean;
}

/** Inline role editor for a team member row. Self-contained: fetches the role catalog and owns the change-role mutation. */
export const TeamMemberRoleSelect: FC<TeamMemberRoleSelectProps> = ({
  memberId,
  roleKey,
  readOnly
}) => {
  const {t} = useTranslation(['team', 'common']);
  const {data: roles = []} = useRolesQuery();
  const changeRoleMutation = useChangeMemberRoleMutation();
  const [isOpen, setIsOpen] = useState(false);

  // The mutation itself settles once the PATCH resolves, but the invalidated members-list
  // refetch (which is what actually updates `roleKey`) lands slightly later — keep the
  // placeholder up until the prop catches up, using the mutation's own response (rather than
  // separately-tracked state) as the source of truth for which role we just set.
  const isChanging =
    changeRoleMutation.isPending ||
    (changeRoleMutation.isSuccess && changeRoleMutation.data.data.roleKey !== roleKey);
  const roleLabel = t(`common:roles.${roleKey}`);
  const dotColorClass = ROLE_DOT_COLOR_CLASS[roleKey] ?? DEFAULT_ROLE_DOT_COLOR_CLASS;

  if (readOnly) {
    return (
      <div className="inline-flex w-fit items-center gap-1.5">
        <Dot colorClassName={dotColorClass} />
        <Typography size="sm" color="secondary">
          {roleLabel}
        </Typography>
      </div>
    );
  }

  const handleSelect = (role: RoleDTO) => {
    setIsOpen(false);
    if (role.key === roleKey) return;

    changeRoleMutation.mutate(
      {memberId, payload: {roleId: role.id}},
      {
        onSuccess: () =>
          showResultToastCard({
            isError: false,
            title: t('roleChangeToast.success.title'),
            description: t('roleChangeToast.success.description', {
              role: t(`common:roles.${role.key}`)
            }),
            closeLabel: t('roleChangeToast.close')
          }),
        onError: error =>
          showResultToastCard({
            isError: true,
            title: t('roleChangeToast.error.title'),
            description: getTranslatedErrorMessage(error, t),
            closeLabel: t('roleChangeToast.close')
          })
      }
    );
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger
        render={
          <button
            type="button"
            disabled={isChanging}
            className="cursor-pointer disabled:cursor-not-allowed"
          >
            <Badge variant="outline" size="sm" className="h-6 w-fit gap-1.5 ps-2 pe-2.5">
              {isChanging ? (
                <>
                  <Skeleton className="size-1.5 shrink-0 rounded-full bg-fg-disabled/40" />
                  <Skeleton className="h-3 w-12 rounded-full bg-fg-disabled/40" />
                </>
              ) : (
                <>
                  <Dot colorClassName={dotColorClass} />
                  {roleLabel}
                </>
              )}
              <PopoverChevron className="size-3" />
            </Badge>
          </button>
        }
      />
      <TeamMemberRolePopoverContent
        roles={roles}
        selectedRoleKey={roleKey}
        onSelect={handleSelect}
      />
    </Popover>
  );
};
